import type {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from 'react';

import type { AreaTag, LayoutPoint, ViewerPosition } from '../contracts/models';
import {
  calculatePolygonCentroid,
  normalizeLayoutPolygon,
} from '../geometry/polygons';

export interface AreaTagPresentation {
  visible?: boolean;
  opacity?: number;
  pointerEvents?: CSSProperties['pointerEvents'];
  baseFill?: string;
  baseFillOpacity?: number;
  fill?: string;
  fillOpacity?: number;
  underlayStroke?: string;
  underlayStrokeOpacity?: number;
  underlayStrokeWidth?: number;
  stroke?: string;
  strokeOpacity?: number;
  strokeWidth?: number;
  cursor?: CSSProperties['cursor'];
  title?: string;
  className?: string;
}

export interface AreaTagLabelRenderContext {
  points: readonly LayoutPoint[];
  centroid: ViewerPosition;
}

export interface AreaTagLayerProps {
  areaTags: readonly AreaTag[];
  isSelected?: (areaTag: AreaTag) => boolean;
  getPoints?: (areaTag: AreaTag) => readonly (readonly number[])[];
  getPresentation?: (areaTag: AreaTag) => AreaTagPresentation;
  renderLabel?: (
    areaTag: AreaTag,
    context: AreaTagLabelRenderContext,
  ) => ReactNode;
  onActivate?: (
    areaTag: AreaTag,
    event: ReactMouseEvent<SVGPolygonElement>,
  ) => void;
  onHoverStart?: (
    areaTag: AreaTag,
    event: ReactMouseEvent<SVGPolygonElement>,
  ) => void;
  onHoverMove?: (
    areaTag: AreaTag,
    event: ReactMouseEvent<SVGPolygonElement>,
  ) => void;
  onHoverEnd?: (
    areaTag: AreaTag,
    event: ReactMouseEvent<SVGPolygonElement>,
  ) => void;
}

const DEFAULT_PRESENTATION: Required<
  Pick<
    AreaTagPresentation,
    | 'opacity'
    | 'baseFill'
    | 'baseFillOpacity'
    | 'fill'
    | 'fillOpacity'
    | 'underlayStroke'
    | 'underlayStrokeOpacity'
    | 'underlayStrokeWidth'
    | 'stroke'
    | 'strokeOpacity'
    | 'strokeWidth'
    | 'cursor'
  >
> = {
  opacity: 1,
  baseFill: '#2563eb',
  baseFillOpacity: 0,
  fill: '#2563eb',
  fillOpacity: 0.2,
  underlayStroke: '#2563eb',
  underlayStrokeOpacity: 0.3,
  underlayStrokeWidth: 2.8,
  stroke: '#2563eb',
  strokeOpacity: 0.92,
  strokeWidth: 2.2,
  cursor: 'pointer',
};

/**
 * Canonical AreaTag polygon layer. Product-specific heat maps, labels, and
 * edit policy are supplied as resolvers and callbacks by the host.
 */
export const AreaTagLayer = ({
  areaTags,
  isSelected = () => false,
  getPoints = (areaTag) => areaTag.points,
  getPresentation = () => DEFAULT_PRESENTATION,
  renderLabel,
  onActivate,
  onHoverStart,
  onHoverMove,
  onHoverEnd,
}: AreaTagLayerProps) => (
  <>
    {areaTags.map((areaTag) => {
      const presentation = {
        ...DEFAULT_PRESENTATION,
        ...getPresentation(areaTag),
      };
      if (presentation.visible === false) return null;

      const points = normalizeLayoutPolygon(getPoints(areaTag));
      if (points.length < 3) return null;

      const selected = isSelected(areaTag);
      const pointList = points.map((point) => point.join(',')).join(' ');
      const centroid = calculatePolygonCentroid(points);

      return (
        <g
          key={areaTag.id}
          className={presentation.className}
          opacity={presentation.opacity}
          style={{
            pointerEvents: presentation.pointerEvents,
            transition: 'opacity 160ms ease',
          }}
        >
          <polygon
            points={pointList}
            fill={presentation.baseFill}
            fillOpacity={presentation.baseFillOpacity}
            stroke="none"
            pointerEvents="none"
          />
          <polygon
            className="unit-polygon-border-underlay"
            points={pointList}
            fill="none"
            stroke={presentation.underlayStroke}
            strokeOpacity={presentation.underlayStrokeOpacity}
            strokeWidth={presentation.underlayStrokeWidth}
            strokeLinejoin="round"
            strokeLinecap="round"
            pointerEvents="none"
          />
          <polygon
            className={`unit-polygon ${selected ? 'selected' : ''}`}
            data-unit-id={areaTag.id}
            data-tour-id="iv-unit-polygon"
            points={pointList}
            fill={presentation.fill}
            fillOpacity={presentation.fillOpacity}
            stroke={presentation.stroke}
            strokeOpacity={presentation.strokeOpacity}
            strokeWidth={presentation.strokeWidth}
            strokeLinejoin="round"
            strokeLinecap="round"
            cursor={presentation.cursor}
            style={{
              transition:
                'fill 180ms ease, stroke 180ms ease, stroke-width 180ms ease, filter 220ms ease',
              filter: 'drop-shadow(0 0 0 rgba(0,0,0,0))',
            }}
            onMouseEnter={(event) => onHoverStart?.(areaTag, event)}
            onMouseMove={(event) => onHoverMove?.(areaTag, event)}
            onMouseLeave={(event) => onHoverEnd?.(areaTag, event)}
            onClick={(event) => onActivate?.(areaTag, event)}
          >
            {presentation.title !== undefined && (
              <title>{presentation.title}</title>
            )}
          </polygon>
          {renderLabel?.(areaTag, { points, centroid })}
        </g>
      );
    })}
  </>
);
